import { Helpers } from '../helpers/Helpers';
import { Module } from '../Module';
import {
  GetDataConnectionAuthInfoRequest,
  IGetDataConnectionAuthInfoParams,
} from '../ServerApi/requests/GetDataConnectionAuthInfoRequest';
import { IAllowedAuthenticationMode, IDataConnectionAuthInfo } from '../ServerApi/types/IDataConnectionAuthInfo';
import { ConnectionAuthService, IAuthenticateDatabaseParams } from '../services/ConnectionAuthService';
import { SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { WindowPopupService } from '../services/WindowPopupService';
import { WindowLocationService } from '../../commonInfra/services/WindowLocationService';
import WebHttpClient from '../../httpClient/WebHttpClient';

export interface IConnectionProperty {
  translate: string;
  value: string;
  tbTestId?: string;
}

export interface IConnectionAuthenticationViewModel {
  selectedAuthMode?: string;
  username?: string;
  password?: string;
  rememberPassword?: boolean;
}

export interface IConnectionAuthenticationScope extends ng.IScope {
  connectionProperties: IConnectionProperty[];
  connectionAuthForm: ng.IFormController;
  attemptAuth: () => void;
  authModeOptions: IAllowedAuthenticationMode[];
  rememberPasswordsForever: boolean;
  focusOnPassword: boolean;
  errorMessageKey: string;
  isPreview: boolean;
  busy: boolean;
  doneWithWindow: boolean;

  authVM: IConnectionAuthenticationViewModel;
  enableRememberPassword: boolean;

  queryParams: ConnectionAuthenticationQueryParams; // Comes from AngularDirective parent scope
  authInfo: IDataConnectionAuthInfo; // Comes from AngularDirective parent scope
}

export interface ConnectionAuthenticationQueryParams extends IGetDataConnectionAuthInfoParams {
  isPreview: string;
}

export class AuthenticateConnectionCtrl {

  public static $inject = [
    '$scope',
    'ConnectionAuthService',
    'WindowPopupService',
    '$location',
    '$window'
  ];

  constructor(
    private $scope: IConnectionAuthenticationScope,
    private connectionAuthService: ConnectionAuthService,
    private windowPopupService: WindowPopupService,
    private $location: ng.ILocationService,
    private $window: ng.IWindowService
  ) {
    $scope.authVM = {};
    $scope.authVM.username = this.$scope.authInfo.defaultUsername;
    $scope.focusOnPassword = !!this.$scope.authInfo.defaultUsername;
    $scope.doneWithWindow = false;
    $scope.rememberPasswordsForever = this.$scope.authInfo.rememberPasswordsForever;
    $scope.enableRememberPassword = this.$scope.authInfo.enableRememberPassword;
    $scope.authVM.rememberPassword = this.$scope.authInfo.rememberPasswordSelectedForDs;

    // If there is 1 allowedAuthenticationMode, then try to get the friendly name if it exists.
    // If there are 2 or more allowedAuthenticationModes then list the options for the user.
    let authModeFriendlyName: string; // Localized authentication mode for the current authentication mode
    if(this.$scope.authInfo.allowedAuthenticationModes) {
      if(this.$scope.authInfo.allowedAuthenticationModes.length === 1) {
        authModeFriendlyName = this.$scope.authInfo.allowedAuthenticationModes[0].modeFriendlyName;
      } else if(this.$scope.authInfo.allowedAuthenticationModes.length >= 2) {
        $scope.authModeOptions = this.$scope.authInfo.allowedAuthenticationModes;
      }
    }
    $scope.authVM.selectedAuthMode = this.$scope.authInfo.authenticationMode;

    $scope.connectionProperties = [];
    const cp = $scope.connectionProperties; // Alias for readability
    if(this.$scope.authInfo.connectionType)     cp.push({ translate: 'authConnectionConnectionType', value: this.$scope.authInfo.connectionTypeDisplayName, tbTestId: 'auth-connection-type' });
    if(authModeFriendlyName)        cp.push({ translate: 'authConnectionAuthentication', value: authModeFriendlyName, tbTestId: 'auth-connection-fixed-auth-mode' });
    if(this.$scope.authInfo.databaseServer)     cp.push({ translate: 'authConnectionServer',         value: this.$scope.authInfo.databaseServer, tbTestId: 'auth-connection-server' });
    if(this.$scope.authInfo.databaseName)       cp.push({ translate: 'authConnectionDatabase',       value: this.$scope.authInfo.databaseName, tbTestId: 'auth-connection-database' });
    if(this.$scope.authInfo.databaseSchema)     cp.push({ translate: 'authConnectionSchema',         value: this.$scope.authInfo.databaseSchema, tbTestId: 'auth-connection-schema' });
    if(this.$scope.authInfo.databaseFilename)   cp.push({ translate: 'authConnectionFilename',       value: this.$scope.authInfo.databaseFilename, tbTestId: 'auth-connection-filename' });
    if(this.$scope.authInfo.samlIdP)            cp.push({ translate: 'authConnectionSAMLIdP',        value: this.$scope.authInfo.samlIdP, tbTestId: 'auth-connection-saml-idp' });

    $scope.attemptAuth = () => {
      $scope.busy = true;
      this.sendDataSourceCredentialsToServer(this.$scope.authInfo, false);
    };

    $scope.isPreview = Helpers.BooleanQueryParamRegex.test($scope.queryParams.isPreview);
  }

  private sendDataSourceCredentialsToServer(authInfo: IDataConnectionAuthInfo, retrying: boolean): void {
    // TODO Figure out the correct response from the authenticate method
      this.connectionAuthService.authenticate(authInfo.actionPath, this.getAuthenticateDatabaseParams())
        .then(
          () => { // Success
            // Force the password input to lose focus before navigating, to work around an iOS Safari issue.
            // For more info, see TFS 564502: Mobile: Cursor isn't dismissed after ...
            $('[tb-test-id="textbox-password-input"]').focusout();

            // Authoring sessions are not opened in a popup window and need to remain open after authenticating
            // But When feature SkipConnectionsDuringLoad is set, then we should just close popup
            // When SkipConnectionsDuringLoad is enabled, URL will have vizqlSessionId param
            const vizqlSessionId = this.$location.search()['vizqlSessionId'];
            const skipConnectionsDuringLoadEnabled = (vizqlSessionId !== undefined);
            const authenticatingForBootstrap = (vizqlSessionId === '0');
            if ((authInfo.isAuthoringSession && !skipConnectionsDuringLoadEnabled) || !this.windowPopupService.maybeNotifyAndClose()) {
              // We can embed auth dialog in case when whole hierarchy of documents belongs to Tableau Server
              // In that case we don't want to reload the page, we want to signal that auth completed.
              // So, if we are here, it means that current page is not popup.
              if (skipConnectionsDuringLoadEnabled && !authenticatingForBootstrap){
                // Just signal that auth completed.
                this.$window.parent.postMessage('tableau.authCompleted', '*');
              } else {
                // If vizqlSessionId is 0, it means it is a bootstrap and we redirect.
                WindowLocationService.navigateTo(this.$scope.queryParams.path);
              }
            }
            // toggle a state that shows a 'close me' message (tfsid 389619)
            // Toggle a state only when this page is opened in popup (tfsid 464123)
            if (this.$window.opener){
                this.$scope.doneWithWindow = true;
            }
            this.$scope.busy = false;

          },
          (error) => { // Error handler
            if (error && error.data && error.data.shouldRetry){
              // Defect 513697:Authentication fails if you open two different "prompt user" sheets in different browser tabs
              if (!retrying){
                // Preventing infinite loop. Only one retry allowed.
                WebHttpClient.sendRequest(new GetDataConnectionAuthInfoRequest(this.$scope.queryParams))
                  .then((result: IDataConnectionAuthInfo) =>
                  {
                    // modulus & exponent might be stale, we just want to retry & not bother user.
                    this.$scope.authInfo = result;
                    this.sendDataSourceCredentialsToServer(result, true);}
                  );

                return;
              }
            }

            // TODO: better error reporting once we figure out what kind of errors to expect
            this.$scope.errorMessageKey = 'errorUnknown';
            this.$scope.busy = false;
          }
        );
  }

  private getAuthenticateDatabaseParams(): IAuthenticateDatabaseParams {
    const authParams: IAuthenticateDatabaseParams = {
      authentication: this.$scope.authVM.selectedAuthMode,
      username:       this.$scope.authVM.username,
      crypted:        rsa.encrypt(this.$scope.authVM.password, { n: this.$scope.authInfo.modulus, e: this.$scope.authInfo.exponent }),
      connection:     this.$scope.authInfo.connection,
      path:           this.$scope.queryParams.path,
      oauthrequest:   false // BUGZID: 408070 Set oauthrequest to false for all non-OAuth scenarios
    };

    if(this.$scope.authVM.rememberPassword) {
      if(this.$scope.authInfo.rememberPasswordsForever) authParams.reuse_forever = true;
      if(!this.$scope.authInfo.rememberPasswordsForever) authParams.reuse = true;
    }

    return authParams;
  }

}

Module.instance().controller('AuthenticateConnectionCtrl', AuthenticateConnectionCtrl);
